import React, { useState, useEffect } from "react";
import {
  Table,
  Spinner,
  Input,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { useHistory } from "react-router-dom";
import { deviceAllSessionAPI } from "../services/Device";
import TableHeader from "../components/TableHeader";

const stimulationCache = {
  data: null,
  timestamp: null,
};

const Stimulation = () => {
  const history = useHistory();
  const [deviceData, setDeviceData] = useState([]);
  const [searchData, setSearchData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterBy, setFilterBy] = useState("SubjectId");
  const [searchValue, setSearchValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 50;

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const validSearchData = searchData.filter((user) => user?.data?.data !== null);
  const totalPages = Math.ceil(validSearchData.length / recordsPerPage);
  const currentRecords = validSearchData.slice(indexOfFirstRecord, indexOfLastRecord);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages]);

  const handleToggle = () => setIsOpen(!isOpen);

  const refreshlist = () => {
    document.getElementById("refreshbtn").className = "fas fa-sync fa-spin";
    stimulationCache.data = null;
    setLoading(true);
    getAllDevice({ showLoader: true });
  };

  const applyFilters = () => {
    if (deviceData.length === 0) return;
    let filteredList = deviceData;
    if (searchValue) {
      filteredList = deviceData.filter((item) => {
        if (item?.data?.data !== null && filterBy === "SubjectId") {
          return item?.data?.data?.subjectId?.toLowerCase()?.includes(searchValue?.toLowerCase());
        } else if (item?.data?.data !== null && filterBy === "Last Stim Recorded") {
          return timeFormatMinSec(item?.data?.data?.updatedAt).includes(searchValue);
        }
      });
    }
    setSearchData(filteredList);
    setCurrentPage(1);
  };

  const convertDataToHex = (data) => parseInt(data, 10).toString(16).padStart(4, "0");

  const getBytes = (hexString) => {
    if (hexString.length !== 4) throw new Error("Hex string must be 4 characters long");
    return { upperByte: hexString.slice(0, 2), lowerByte: hexString.slice(2, 4) };
  };

  const handleFilterByChange = (value) => {
    setSearchValue("");
    setFilterBy(value);
  };

  const handleSearchInputChange = (e) => setSearchValue(e.target.value);

  const identifyUsersWithInactiveStim = (deviceData) => {
    const groupedData = deviceData.reduce((groups, item) => {
      const { prcId } = item;
      if (!groups[prcId]) groups[prcId] = [];
      groups[prcId].push(item);
      return groups;
    }, {});

    return Object.entries(groupedData).map(([prcId, data]) => {
      const filteredData = data
        .map((item) => {
          const hexData = convertDataToHex(item.data);
          const { upperByte, lowerByte } = getBytes(hexData);
          return { ...item, upperByte, lowerByte };
        })
        .filter((item) => item.lowerByte > 0);

      const length = filteredData?.length;
      if (filteredData.length >= 2) {
        const firstData = parseInt(filteredData[length - 1]?.upperByte);
        const secondData = parseInt(filteredData[length - 2]?.upperByte);
        return {
          prcId,
          data: {
            data: filteredData[length - 1],
            groupedData,
            stimUp: firstData > secondData ? "Yes" : "No",
            stimDown: firstData < secondData ? "Yes" : "No",
            stimNoChange: firstData === secondData ? "Yes" : "No",
          },
        };
      } else if (filteredData.length === 1) {
        return { prcId, data: { data: filteredData[0], groupedData, stimUp: "No", stimDown: "No", stimNoChange: "Yes" } };
      } else {
        return { prcId, data: { data: null, groupedData, stimUp: "No", stimDown: "No", stimNoChange: "No" } };
      }
    });
  };

  useEffect(() => {
    applyFilters();
  }, [filterBy, searchValue, deviceData]);

  function downloadCSV(csv, filename) {
    var csvFile = new Blob([csv], { type: "text/csv" });
    var downloadLink = document.createElement("a");
    downloadLink.download = filename;
    downloadLink.href = window.URL.createObjectURL(csvFile);
    downloadLink.style.display = "none";
    document.body.appendChild(downloadLink);
    downloadLink.click();
  }

  function exportTableToCSV() {
    let csv = [];
    let rows = document.querySelectorAll("table tr");
    for (let i = 0; i < rows.length; i++) {
      let row = [], cols = rows[i].querySelectorAll("td, th");
      for (let j = 0; j < cols.length; j++) row.push(cols[j].innerText);
      csv.push(row.join(","));
    }
    downloadCSV(csv.join("\n"), "Stimulation");
  }

  const getAllDevice = async ({ showLoader = false, isBackground = false } = {}) => {
    try {
      if (showLoader) setLoading(true);
      const data = await deviceAllSessionAPI(100, null, "EVENT_TRT_ACTIVE");
      if (data.success) {
        const usersWithActiveStim = identifyUsersWithInactiveStim(data.sessions);
        stimulationCache.data = usersWithActiveStim;
        setDeviceData(usersWithActiveStim);
        if (!isBackground) setSearchData(usersWithActiveStim);
      }
    } catch (error) {
      console.error("Stimulation load failed:", error);
    } finally {
      setLoading(false);
      const btn = document.getElementById("refreshbtn");
      if (btn) btn.className = "fas fa-sync fa-stop";
    }
  };

  useEffect(() => {
    if (stimulationCache.data) {
      setDeviceData(stimulationCache.data);
      setSearchData(stimulationCache.data);
      getAllDevice({ isBackground: true });
    } else {
      getAllDevice({ showLoader: true });
    }
  }, []);

  const timeFormatMinSec = (dateStr) => {
    if (!dateStr || dateStr == "undefined" || dateStr < 1520160669214) return " ";
    let date = new Date(parseInt(dateStr));
    let hours = date.getHours();
    let minutes = date.getMinutes();
    let ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    minutes = minutes < 10 ? "0" + minutes : minutes;
    let strTime = (" 0" + hours).slice(-2) + ":" + ("0" + minutes).slice(-2) + " " + ampm;
    let newDate = new Date(parseInt(dateStr)).toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" });
    return newDate + " (" + strTime + ")";
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const deviceListRecords = () => {
    let sortedList = [...currentRecords];
    if (sortColumn) {
      sortedList.sort((a, b) => {
        const aVal = a?.data?.data?.[sortColumn] || "";
        const bVal = b?.data?.data?.[sortColumn] || "";
        return sortOrder === "asc"
          ? aVal.toString().localeCompare(bVal.toString())
          : bVal.toString().localeCompare(aVal.toString());
      });
    }

    if (loading) {
      return (
        <tr>
          <td colSpan="7" style={{ textAlign: "center", padding: "60px 0" }}>
            <Spinner animation="border" style={{ width: "2.5rem", height: "2.5rem", color: "#525f7f" }} />
            <div style={{ marginTop: 12, color: "#6c757d", fontSize: 14 }}>Loading...</div>
          </td>
        </tr>
      );
    }

    if (sortedList?.length === 0) {
      return (
        <tr>
          <td colSpan="7" style={{ textAlign: "center", padding: "60px 0", color: "#6c757d" }}>
            No Record Found!
          </td>
        </tr>
      );
    }

   return sortedList.map((user, index) => {
  const handleClick = (event) => {
    event.preventDefault();
    history.push({ pathname: `/device/${user?.data?.data?.deviceId}` });
  };
      return (
        <tr key={index}>
          <td style={{ textAlign: "left" }}>
            <a href="#" onClick={handleClick}>{user?.data?.data?.prcId || "-"}</a>
          </td>
          <td style={{ textAlign: "left" }}>{user?.data?.data?.subjectId || "-"}</td>
          <td style={{ textAlign: "left" }}>{timeFormatMinSec(user?.data?.data?.updatedAt) || "-"}</td>
          <td style={{ textAlign: "left" }}>
            {user?.data?.data?.upperByte === "00" || user?.data?.data?.upperByte === "0e"
              ? `Stimulation OFF (${user?.data?.data?.upperByte})`
              : user?.data?.data?.upperByte > 9
                ? `High Amplitude (${user?.data?.data?.upperByte})`
                : `Amplitude (${user?.data?.data?.upperByte})`}
          </td>
          <td style={{ textAlign: "left" }}>{user?.data?.stimUp === "Yes" ? "Stimulation Increased" : "No"}</td>
          <td style={{ textAlign: "left" }}>{user?.data?.stimDown === "Yes" ? "Stimulation Decreased" : "No"}</td>
          <td style={{ textAlign: "left" }}>{user?.data?.stimNoChange === "Yes" ? "No Change in Stimulation" : "No"}</td>
        </tr>
      );
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", background: "#fff", height: "100vh", overflow: "hidden" }}>
      <TableHeader
  title="Stimulation Usage Summary per Subject"
  onRefresh={refreshlist}
  onDownload={exportTableToCSV}
  totalCount={validSearchData.length > 0 ? validSearchData.length : null}
  hasMore={false}
       filterContent={
  <div style={{ display: "flex", gap: 8, alignItems: "center", maxWidth: 500 }}>
    <Dropdown isOpen={isOpen} toggle={handleToggle} style={{ flexShrink: 0 }}>
      <DropdownToggle caret>Filter By: {filterBy}</DropdownToggle>
      <DropdownMenu>
        <DropdownItem onClick={() => handleFilterByChange("SubjectId")}>SubjectId</DropdownItem>
        <DropdownItem onClick={() => handleFilterByChange("Last Stim Recorded")}>Last Stim Recorded</DropdownItem>
      </DropdownMenu>
    </Dropdown>
    <Input
      type="text"
      placeholder={`Enter ${filterBy}`}
      value={searchValue}
      onChange={handleSearchInputChange}
      style={{ flex: 1 }}
    />
  </div>
}
      />

      <div style={{ overflowY: "auto", background: "#fff" }}>
        <Table striped hover responsive bordered className="align-items-center table-flush" style={{ margin: 0 }}>
          <thead className="thead-light" style={{ cursor: "pointer", position: "sticky", top: 0, zIndex: 1 }}>
            <tr>
              <th scope="col" style={{ textAlign: "left" }} onClick={() => handleSort("prcId")}>Ring Id</th>
              <th scope="col" style={{ textAlign: "left" }} onClick={() => handleSort("subjectId")}>Subject Id</th>
              <th scope="col" style={{ textAlign: "left" }} onClick={() => handleSort("updatedAt")}>Last Stim Date/Time</th>
              <th scope="col" style={{ textAlign: "left" }} onClick={() => handleSort("upperByte")}>Stimulation Level Change Events</th>
              <th scope="col" style={{ textAlign: "left" }} onClick={() => handleSort("stimUp")}>Stimulation Increased</th>
              <th scope="col" style={{ textAlign: "left" }} onClick={() => handleSort("stimDown")}>Stimulation Decreased</th>
              <th scope="col" style={{ textAlign: "left" }} onClick={() => handleSort("stimNoChange")}>No Change in Stimulation</th>
            </tr>
          </thead>
          <tbody>
            {deviceListRecords()}
            {validSearchData.length > 50 && !loading && (
              <tr>
                <td colSpan="7" style={{ textAlign: "right", padding: "12px 16px" }}>
                  <div style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                    <button
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(prev => prev - 1)}
                      style={{
                        padding: "6px 14px", borderRadius: 6, border: "1px solid #dee2e6",
                        background: currentPage === 1 ? "#f8f9fa" : "#fff",
                        cursor: currentPage === 1 ? "not-allowed" : "pointer", fontWeight: 500,
                      }}
                    >←</button>
                   <div style={{ padding: "6px 14px", borderRadius: 6, background: "#f2f0f0", color: "#0e0d0d", fontWeight: 500, fontSize: 14 }}>
  {indexOfFirstRecord + 1}–{Math.min(indexOfLastRecord, validSearchData.length)} of {validSearchData.length}
</div>
                    <button
                      disabled={currentPage === totalPages || totalPages === 0}
                      onClick={() => setCurrentPage(prev => prev + 1)}
                      style={{
                        padding: "6px 14px", borderRadius: 6, border: "1px solid #dee2e6",
                        background: currentPage === totalPages ? "#f8f9fa" : "#fff",
                        cursor: currentPage === totalPages ? "not-allowed" : "pointer", fontWeight: 500,
                      }}
                    >→</button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default Stimulation;